import type { CompetitorBoardSummaryDto } from '@up2stay/up2promo-api-client';
import {
  Card,
  cn,
  previewPanelSurfaceClass,
  Skeleton,
} from '@up2stay/up2promo-ui';
import { Sparkles } from 'lucide-react';

import { useRelativeTime } from '@/core/components/leads/lead-detail/format-relative-time';
import type { Dictionary } from '@/core/providers/dictionary-provider';

type Dict = Dictionary['reputation']['competitorsPage']['briefing'];

const SECTIONS = [
  { key: 'quality', eyebrow: 'text-muted-foreground' },
  { key: 'volume', eyebrow: 'text-[color:var(--warn-fg)]' },
  { key: 'lever', eyebrow: 'text-[color:var(--ok-fg)]' },
] as const;

/** AI competitive briefing in three parts: quality, volume, lever. */
export function CompetitorBriefingCard({
  summary,
  loading,
  dict,
  locale,
}: {
  summary: CompetitorBoardSummaryDto | undefined;
  loading: boolean;
  dict: Dict;
  locale: string;
}) {
  const ready = summary?.status === 'ready';
  const updated = useRelativeTime(ready ? summary?.generatedAt : null, locale);

  return (
    <Card
      className={cn(
        'flex flex-col gap-4 p-5 shadow-none sm:p-6',
        previewPanelSurfaceClass,
      )}
    >
      <div className="flex items-center gap-2.5">
        <span className="flex size-[30px] items-center justify-center rounded-[9px] bg-[color:var(--ghost-accent-icon-bg)] text-[color:var(--ghost-accent-fg)]">
          <Sparkles className="size-4" aria-hidden />
        </span>
        <h2 className="flex-1 text-[15.5px] font-bold sm:text-[17px]">
          {dict.title}
        </h2>
        {updated ? (
          <span className="text-muted-foreground text-[11.5px]">
            {dict.updatedAgo.replace('{time}', updated)}
          </span>
        ) : null}
      </div>

      {loading ? (
        <div className="grid gap-3 md:grid-cols-3">
          {SECTIONS.map(({ key }) => (
            <Skeleton
              key={key}
              className="h-28 rounded-[var(--radius-nested)]"
            />
          ))}
        </div>
      ) : ready && summary ? (
        <div className="grid gap-3 md:grid-cols-3">
          {SECTIONS.map(({ key, eyebrow }) => {
            const section = summary[key];
            if (!section) return null;
            return (
              <div
                key={key}
                className="rounded-[var(--radius-nested)] border bg-card p-4"
              >
                <div
                  className={cn(
                    'text-[11px] font-semibold uppercase tracking-[0.08em]',
                    eyebrow,
                  )}
                >
                  {dict[key]}
                </div>
                <div className="mt-1.5 text-[15px] font-bold">
                  {section.headline}
                </div>
                <p className="text-muted-foreground mt-1 text-[13px] leading-normal">
                  {section.body}
                </p>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-muted-foreground text-[13.5px]">
          {dict.unavailable}
        </p>
      )}
    </Card>
  );
}
